import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Avatar,
  message
} from 'antd';
import {
  UserOutlined,
  TrophyOutlined,
  ReloadOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  TeamOutlined,
  StarOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [hoursData, setHoursData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using fetch
  const fetchHoursData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/teacher-reports/hours?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch hours data');
    return await response.json();
  };

  const fetchEarningsData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/teacher-reports/earnings?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch earnings data');
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [hours, earnings] = await Promise.all([
        fetchHoursData(),
        fetchEarningsData()
      ]);
      
      setHoursData(hours);
      setEarningsData(earnings);
      
      message.success('Teacher report data loaded successfully');
    } catch (err) {
      console.error('Error loading teacher report data:', err);
      
      let errorMessage = 'Failed to load teacher report data';
      if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('teacher-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teacher Performance Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Prepare chart data
const topTeachersChartData = hoursData?.top_teachers?.slice(0, 10).map(teacher => {
  const name = teacher.nickname || 'Unknown';
  return {
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    hours: parseFloat(teacher.total_hours) || 0
  };
}) || [];


const earningsChartData = earningsData?.slice(0, 10).map(teacher => {
  const name = teacher.nickname || 'Unknown';
  return {
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    earnings: parseFloat(teacher.total_earning) || 0
  };
}) || [];


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {payload[0].dataKey === 'hours' 
              ? `Hours: ${payload[0].value}` 
              : `Earnings: $${payload[0].value}`
            }
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate summary stats
  const totalTeachers = hoursData?.top_teachers?.length || 0;
  const averageHours = parseFloat(hoursData?.average_hours) || 0;
  const totalEarnings = earningsData?.reduce((sum, teacher) => sum + parseFloat(teacher.total_earning || 0), 0) || 0;
  const averageEarnings = earningsData?.length ? totalEarnings / earningsData.length : 0;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="teacher-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Teacher Performance Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Teacher Performance Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of teacher hours and earnings performance
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!hoursData && !earningsData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Teachers"
                  value={totalTeachers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Average Hours"
                  value={averageHours}
                  precision={2}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Earnings"
                  value={totalEarnings}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Earnings"
                  value={averageEarnings}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Top Teachers by Hours Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    Top Teachers by Hours
                  </Space>
                }
              >
                {topTeachersChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topTeachersChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="hours" fill="#52c41a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No teaching hours data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Top Earners Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    Top Earners
                  </Space>
                }
              >
                {earningsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={earningsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="earnings" fill="#722ed1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No earnings data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
  {/* Teaching Hours Cards */}
  <Col xs={24} lg={12}>
    <Card title="Teaching Hours Leaderboard">
      {hoursData?.top_teachers?.length > 0 ? (
        <Row gutter={[16, 16]}>
          {hoursData.top_teachers.map((teacher, index) => (
            <Col span={24} key={teacher.teacher_id}>
              <Card>
                <Space align="start">
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: index === 0 ? '#faad14' : '#1890ff',
                      border: index === 0 ? '2px solid #faad14' : 'none'
                    }}
                  />
                  <div>
                    <Text strong>
                      {teacher.nickname || teacher.teacher_name}
                      {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
                    </Text>
                    <br />
                    <Text type="secondary">Rank: {index + 1}</Text>
                    <br />
                    <Text>Total Hours: {parseFloat(teacher.total_hours).toFixed(2)}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">No teaching hours data available</Text>
        </div>
      )}
    </Card>
  </Col>

  {/* Earnings Cards */}
  <Col xs={24} lg={12}>
    <Card title="Earnings Leaderboard">
      {earningsData?.length > 0 ? (
        <Row gutter={[16, 16]}>
          {earningsData.map((teacher, index) => (
            <Col span={24} key={teacher.teacher_id}>
              <Card>
                <Space align="start">
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: index === 0 ? '#faad14' : '#52c41a',
                      border: index === 0 ? '2px solid #faad14' : 'none'
                    }}
                  />
                  <div>
                    <Text strong>
                      {teacher.nickname || teacher.teacher_name}
                      {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
                    </Text>
                    <br />
                    <Text type="secondary">Rank: {index + 1}</Text>
                    <br />
                    <Text>Total Earnings: {parseFloat(teacher.total_earning).toFixed(2)}</Text>
                    <br />
                    <Text>Classes: {teacher.classes_count}</Text>
                    <br />
                    <Text>Avg per Class: {parseFloat(teacher.avg_earning_per_class).toFixed(2)}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">No earnings data available</Text>
        </div>
      )}
    </Card>
  </Col>
</Row>

        </Spin>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(TeacherReport, ['superadmin']);